'use client';

import { Logo } from '@/components/logo';
import { Menu, X as CloseIcon, Bike, Wrench, Mountain, CalendarCheck } from 'lucide-react';
import React from 'react';
import LocaleSwitcherSubtle from '@/components/locale-switcher-subtle';
import { SeasonSwitcher } from '@/components/season-switcher';
import { siteConfig } from '@/config/site';
import { useTranslations, useLocale } from 'next-intl';
import { getLocalizedSlug, type LocaleCode } from '@/lib/localized-slugs';
import { usePathname } from 'next/navigation';
import { useSeason } from '@/contexts/season-context';
export const MainNavigation = ({
  locale: localeProp
}: {
  locale?: string;
}) => {
  const [menuState, setMenuState] = React.useState(false);
  const pathname = usePathname();
  const t = useTranslations('Header');
  const localeFromHook = useLocale();
  const {
    season,
    setSeason
  } = useSeason();

  // Extract locale from pathname as most reliable source
  const localeFromPath = pathname?.split('/')[1] || 'en';
  const validLocales = ['en', 'fr', 'nl', 'es', 'it'];
  // Use locale from pathname (most reliable), then prop, then hook, then fallback
  const locale = validLocales.includes(localeFromPath) ? localeFromPath : localeProp || localeFromHook || 'en';

  // Get the localized contact slugs for current locale
  const summerContactSlug = getLocalizedSlug('contact-summer', locale as LocaleCode);
  const winterContactSlug = getLocalizedSlug('contact-winter', locale as LocaleCode);

  // Get the localized 7laux page slugs
  const summer7LauxSlug = getLocalizedSlug('7laux-summer', locale as LocaleCode);
  const winter7LauxSlug = getLocalizedSlug('7laux-winter', locale as LocaleCode);

  // Get the localized slugs for new separate rental and workshop pages
  const locationSummerSlug = getLocalizedSlug('location-summer', locale as LocaleCode);
  const locationWinterSlug = getLocalizedSlug('location-winter', locale as LocaleCode);
  const atelierSummerSlug = getLocalizedSlug('atelier-summer', locale as LocaleCode);
  const atelierWinterSlug = getLocalizedSlug('atelier-winter', locale as LocaleCode);

  // Get the localized homepage slug
  const homeSlug = getLocalizedSlug('7laux', locale as LocaleCode);

  // Check if we're on a contact page by checking against localized slugs
  const isContactPage = pathname?.includes(summerContactSlug) || pathname?.includes(winterContactSlug) || pathname?.includes('/contact/summer') || pathname?.includes('/contact/winter');

  // Check if we're on 7laux info page (not contact, not location pages)
  const is7LauxPage = (pathname?.includes(summer7LauxSlug) || pathname?.includes(winter7LauxSlug) || pathname?.includes('/7laux/summer') || pathname?.includes('/7laux/winter')) && !isContactPage && !pathname?.includes('location-vtt') && !pathname?.includes('location-ski');

  // Check if we're on the new location (rental) page - check both SEO slugs and actual paths
  const isLocationPage = pathname?.includes(locationSummerSlug) || pathname?.includes(locationWinterSlug) || pathname?.includes('/location/summer') || pathname?.includes('/location/winter');

  // Check if we're on the new atelier (workshop) page - check both SEO slugs and actual paths
  const isAtelierPage = pathname?.includes(atelierSummerSlug) || pathname?.includes(atelierWinterSlug) || pathname?.includes('/atelier/summer') || pathname?.includes('/atelier/winter');

  // Determine default season based on current date (after November 1st = winter)
  const getDefaultSeason = (): 'summer' | 'winter' => {
    const now = new Date();
    const month = now.getMonth(); // 0-11 (0 = January)
    // November 1st onwards = winter (month 10 is November)
    return month >= 10 || month <= 3 ? 'winter' : 'summer';
  };

  // Check if we're on homepage first (need this before route generation)
  const pathWithoutLocale = pathname?.replace(`/${locale}`, '') || '/';
  const isHomePage = pathWithoutLocale === '/' || pathWithoutLocale === `/${homeSlug}`;

  // Use default season if on homepage, otherwise use current season
  const effectiveSeason = isHomePage ? getDefaultSeason() : season || 'summer';

  // Get the 7laux page route based on effective season
  const sevenLauxRoute = effectiveSeason === 'winter' ? winter7LauxSlug : summer7LauxSlug;

  // Get the location (rental) page route based on effective season
  const locationRoute = effectiveSeason === 'winter' ? locationWinterSlug : locationSummerSlug;

  // Get the atelier (workshop) page route based on effective season
  const atelierRoute = effectiveSeason === 'winter' ? atelierWinterSlug : atelierSummerSlug;

  // Helper function to check if a nav item is active
  const isNavItemActive = (href: string) => {
    if (!pathname) return false;

    // Remove locale prefix from pathname for comparison
    const pathWithoutLocale_0 = pathname.replace(`/${locale}`, '') || '/';

    // Homepage detection - nothing should be selected on homepage
    const isRealHomePage = pathWithoutLocale_0 === '/' || pathWithoutLocale_0 === `/${homeSlug}`;
    if (href === '/') {
      // Never highlight home in navigation - homepage should have nothing selected
      return false;
    }
    if (href === '/7laux') {
      // For 7laux info pages, check using actual slugs (but NOT homepage)
      return is7LauxPage && !isRealHomePage;
    }
    if (href === '/location') {
      // For location (rental), check using simple paths
      return isLocationPage;
    }
    if (href === '/atelier') {
      // For atelier (workshop), check using simple paths
      return isAtelierPage;
    }
    // For other items, check if pathname includes the href
    return pathWithoutLocale_0.includes(href);
  };

  // Get the contact page route based on effective season
  // Open the contact form on the tab matching where the user came from
  const contactTab = isLocationPage ? 'rental' : isAtelierPage ? 'workshop' : null;
  const contactSlug = effectiveSeason === 'winter' ? winterContactSlug : summerContactSlug;
  const contactRoute = `/${locale}/${contactSlug}${contactTab ? `?tab=${contactTab}` : ''}`;

  // Handle season change - reload page if on seasonal pages
  const handleSeasonChange = (newSeason: 'summer' | 'winter') => {
    if (isContactPage) {
      // If on contact page, navigate to the new season's contact page using localized slug
      const newContactSlug = newSeason === 'summer' ? getLocalizedSlug('contact-summer', locale as LocaleCode) : getLocalizedSlug('contact-winter', locale as LocaleCode);

      // Preserve the tab query parameter if present
      const searchParams = new URLSearchParams(window.location.search);
      const tabParam = searchParams.get('tab');
      const fullUrl = `/${locale}/${newContactSlug}${tabParam ? `?tab=${tabParam}` : ''}`;

      // Force a full page reload
      window.location.href = fullUrl;
    } else if (is7LauxPage) {
      // If on 7laux page, navigate to the new season's 7laux page using localized slug
      const new7LauxSlug = newSeason === 'summer' ? getLocalizedSlug('7laux-summer', locale as LocaleCode) : getLocalizedSlug('7laux-winter', locale as LocaleCode);
      const fullUrl_0 = `/${locale}/${new7LauxSlug}`;

      // Force a full page reload
      window.location.href = fullUrl_0;
    } else if (isLocationPage) {
      // If on location (rental) page, navigate to the new season's location page
      const newSlug = newSeason === 'summer' ? getLocalizedSlug('location-summer', locale as LocaleCode) : getLocalizedSlug('location-winter', locale as LocaleCode);
      const fullUrl_1 = `/${locale}/${newSlug}`;

      // Force a full page reload
      window.location.href = fullUrl_1;
    } else if (isAtelierPage) {
      // If on atelier (workshop) page, navigate to the new season's atelier page
      const newSlug_0 = newSeason === 'summer' ? getLocalizedSlug('atelier-summer', locale as LocaleCode) : getLocalizedSlug('atelier-winter', locale as LocaleCode);
      const fullUrl_2 = `/${locale}/${newSlug_0}`;

      // Force a full page reload
      window.location.href = fullUrl_2;
    } else if (isHomePage) {
      // If on homepage, navigate to the new season's Location (rental) page
      const newSlug_1 = newSeason === 'summer' ? getLocalizedSlug('location-summer', locale as LocaleCode) : getLocalizedSlug('location-winter', locale as LocaleCode);
      const fullUrl_3 = `/${locale}/${newSlug_1}`;

      // Force a full page reload
      window.location.href = fullUrl_3;
    } else {
      // If not on a seasonal page, just update the state
      setSeason(newSeason);
    }
  };

  // Single, uniformly-styled set of links for the mobile dropdown (one visual group).
  const mobilePills = [{
    key: 'location',
    href: `/${locale}/${locationRoute}`,
    Icon: Bike,
    active: isLocationPage
  }, {
    key: 'atelier',
    href: `/${locale}/${atelierRoute}`,
    Icon: Wrench,
    active: isAtelierPage
  }, {
    key: '7laux',
    href: `/${locale}/${sevenLauxRoute}`,
    Icon: Mountain,
    active: is7LauxPage
  }, {
    key: 'bookingContact',
    href: contactRoute,
    Icon: CalendarCheck,
    active: isContactPage
  }];
  return <header>
      {/* translateZ(0): own compositing layer so Safari doesn't inset the fixed
          bar inside the body's overflow-x:clip context (WebKit clips fixed
          descendants of an overflow container -> left/top gap). */}
      <nav data-state={menuState && 'active'} className="fixed inset-x-0 top-0 z-40 w-full [transform:translateZ(0)]">
        {/* Green bar — full width, stays bar-height (never grows on mobile) */}
        <div className="w-full border-b border-primary/20 bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to backdrop-blur-3xl">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="relative flex items-center justify-between gap-2 py-3 xl:py-4">
            {/* Left - Season switcher (always visible) + desktop links */}
            <div className="flex items-center gap-3 xl:gap-6">
              <SeasonSwitcher season={isHomePage ? getDefaultSeason() : season} setSeason={handleSeasonChange} />

              <ul className="hidden xl:flex gap-8 text-sm">
                {siteConfig.mainNav.map((item, index) => {
                  const isActive = isNavItemActive(item.href);
                  let itemHref = item.href as string;
                  if (item.href === '/location') {
                    itemHref = `/${locale}/${locationRoute}`;
                  } else if (item.href === '/atelier') {
                    itemHref = `/${locale}/${atelierRoute}`;
                  }
                  return <li key={index}>
                      <a href={itemHref} className={`block px-3 py-2 rounded-lg transition-all duration-200 ${isActive ? 'bg-white/25 text-white font-semibold shadow-md backdrop-blur-sm' : 'text-white/90 hover:text-white hover:bg-white/15'}`}>
                        <span>{t(item.key)}</span>
                      </a>
                    </li>;
                })}
              </ul>
            </div>

            {/* Center - Logo: touches viewport top, larger, overhangs the bar */}
            <a href={`/${locale}`} aria-label="home" className="absolute left-1/2 top-0 z-10 flex -translate-x-1/2 items-start justify-center bg-[url('/leather.jpg')] bg-cover bg-center px-3 pt-1 pb-2 rounded-b-xl border border-t-0 border-[#2a1f10]/50 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),inset_0_-2px_3px_rgba(0,0,0,0.3),0_6px_16px_rgba(0,0,0,0.45)] xl:px-4 xl:pt-1 xl:pb-3">
              <Logo className="h-16 sm:h-20 xl:h-28 w-auto drop-shadow-md" />
            </a>

            {/* Right - Desktop actions + locale + mobile menu button */}
            <div className="flex items-center gap-2">
              <div className="hidden xl:flex items-center gap-2">
                <a href={`/${locale}/${sevenLauxRoute}`} className={`px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium whitespace-nowrap ${is7LauxPage ? 'bg-white/25 text-white shadow-md backdrop-blur-sm' : 'text-white/90 hover:text-white hover:bg-white/15'}`} aria-label="7 Laux">
                  <span>{t('7laux')}</span>
                </a>
                <a href={contactRoute} className={`px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium whitespace-nowrap ${isContactPage ? 'bg-white/25 text-white shadow-md backdrop-blur-sm' : 'text-white/90 hover:text-white hover:bg-white/15'}`} aria-label="Booking & Contact">
                  <span>{t('bookingContact')}</span>
                </a>
              </div>

              <LocaleSwitcherSubtle currentLocale={locale} />

              {/* Mobile menu button - icon only */}
              <button onClick={() => setMenuState(!menuState)} aria-label={menuState ? t('closeMenu') : t('openMenu')} aria-expanded={menuState} className="relative flex size-10 items-center justify-center rounded-lg bg-white/20 text-white shadow-sm transition-all duration-200 hover:bg-white/30 xl:hidden">
                <Menu className="in-data-[state=active]:rotate-180 in-data-[state=active]:scale-0 in-data-[state=active]:opacity-0 size-5 duration-200" />
                <CloseIcon className="in-data-[state=active]:rotate-0 in-data-[state=active]:scale-100 in-data-[state=active]:opacity-100 absolute size-5 -rotate-180 scale-0 opacity-0 duration-200" />
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Mobile dropdown — full-width dark-green panel, white pills */}
        <div className="in-data-[state=active]:block hidden xl:hidden w-full border-b border-black/25 bg-[oklch(0.19_0.10_156)] shadow-lg">
          <div className="px-4 pt-12 pb-9 sm:px-6">
            <ul className="flex flex-wrap justify-center gap-2">
              {mobilePills.map(({
              key,
              href: href_0,
              Icon,
              active
            }) => <li key={key}>
                  <a href={href_0} onClick={() => setMenuState(false)} className={`inline-flex items-center gap-2 rounded-full border py-2 pl-2 pr-4 text-sm font-medium transition-all duration-200 ${active ? 'border-transparent bg-white text-primary font-bold shadow-lg' : 'border-white/25 bg-white/10 text-white hover:bg-white/20 hover:border-white/45'}`}>
                    <span className={`flex size-7 shrink-0 items-center justify-center rounded-full ${active ? 'bg-primary/15 text-primary' : 'bg-white/15 text-white'}`}>
                      <Icon className="size-4" />
                    </span>
                    <span>{t(key)}</span>
                  </a>
                </li>)}
            </ul>
          </div>
        </div>
      </nav>
    </header>;
};